import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Drawer} from '@dropbox/dig-components/drawer';
import {Split} from '@dropbox/dig-foundations';
import {UIIcon, UIIconProps} from '@dropbox/dig-icons';
import {ArrowLeftLine, CloseLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {useEffect} from 'react';

export const DrawerContainer = ({
  icon,
  open,
  header,
  footer,
  children,
  preventEscapeClose,
  backButton,
  onClose,
  fillHeader,
}: {
  open: boolean;
  icon?: UIIconProps['src'];
  header: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  preventEscapeClose?: boolean;
  backButton?: boolean;
  onClose?: () => void;
  fillHeader?: boolean;
}) => {
  useEffect(() => {
    // Add a listener to keyboard esc key to close the live preview drawer
    const handleKeyDown = (event: KeyboardEvent) => {
      if (preventEscapeClose) {
        return;
      }
      if (!open || !onClose) {
        return;
      }
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open, onClose, preventEscapeClose]);

  return (
    <Drawer.Container>
      <Drawer.Header>
        <Drawer.Layout style={{alignItems: 'flex-start'}}>
          <Drawer.LayoutItem shift="left" width="fill">
            {backButton && onClose && (
              <Drawer.LayoutItem shift="left">
                <Button
                  variant="borderless"
                  onClick={onClose}
                  size="small"
                  withIconStart={<UIIcon src={ArrowLeftLine} />}
                  style={{marginLeft: -8, marginBottom: 8}}
                >
                  {t('back')}
                </Button>
              </Drawer.LayoutItem>
            )}
            <Split alignY="top" gap="4">
              {icon && (
                <Split.Item>
                  <UIIcon src={icon} />
                </Split.Item>
              )}
              <Split.Item width={fillHeader ? 'fill' : undefined} willCollapse={fillHeader}>
                {header}
              </Split.Item>
            </Split>
          </Drawer.LayoutItem>
          {!backButton && onClose && (
            <Drawer.LayoutItem shift="right">
              <IconButton variant="borderless" onClick={onClose}>
                <UIIcon src={CloseLine} />
              </IconButton>
            </Drawer.LayoutItem>
          )}
        </Drawer.Layout>
      </Drawer.Header>
      <Drawer.Body>{children}</Drawer.Body>
      {footer && <Drawer.Footer>{footer}</Drawer.Footer>}
    </Drawer.Container>
  );
};
